import { useEffect, useState } from 'react';
import { SiteSettings, apiClient } from './api';

/**
 * Утилиты для работы с настройками виджетов
 */

export interface WidgetSettings {
  opacity: number;
  borderRadius: number;
  shadowIntensity: number;
  primaryColor: string;
  secondaryColor: string;
  backgroundBlur: boolean;
  hoverEffects: boolean;
  animationDuration: number;
}

export interface NewsCardSettings {
  layout: 'default' | 'compact' | 'featured';
  showAuthor: boolean;
  showDate: boolean;
  showTags: boolean;
}

export interface ParticleSettings {
  enabled: boolean;
  count: number;
  color: string;
  speed: number;
  size: number;
}

export interface HoverSettings {
  opacity: number;
  scale: number;
}

/**
 * Преобразует настройки сайта в настройки виджетов
 */
export function getWidgetSettings(settings: SiteSettings): WidgetSettings {
  return {
    opacity: settings.widget_opacity,
    borderRadius: settings.widget_border_radius,
    shadowIntensity: settings.widget_shadow_intensity,
    primaryColor: settings.widget_primary_color,
    secondaryColor: settings.widget_secondary_color,
    backgroundBlur: settings.widget_background_blur,
    hoverEffects: settings.widget_hover_effects,
    animationDuration: settings.widget_animation_duration
  };
}

/**
 * Преобразует настройки сайта в настройки новостных карточек
 */
export function getNewsCardSettings(settings: SiteSettings): NewsCardSettings {
  return {
    layout: settings.news_card_layout,
    showAuthor: settings.news_card_show_author,
    showDate: settings.news_card_show_date,
    showTags: settings.news_card_show_tags
  };
}

/**
 * Преобразует настройки сайта в настройки частиц
 */
export function getParticleSettings(settings: SiteSettings): ParticleSettings {
  return {
    enabled: settings.widget_particles_enabled,
    count: settings.widget_particles_count,
    color: settings.widget_particles_color,
    speed: settings.widget_particles_speed,
    size: settings.widget_particles_size
  };
}

/**
 * Преобразует настройки сайта в настройки hover-эффектов
 */
export function getHoverSettings(settings: SiteSettings): HoverSettings {
  return {
    opacity: settings.widget_hover_opacity,
    scale: settings.widget_hover_scale
  };
}

/**
 * Генерирует CSS переменные для настроек виджетов
 */
export function generateWidgetCSSVariables(settings: SiteSettings): Record<string, string> {
  const widgetSettings = getWidgetSettings(settings);
  const hoverSettings = getHoverSettings(settings);

  return {
    '--widget-opacity': widgetSettings.opacity.toString(),
    '--widget-border-radius': `${widgetSettings.borderRadius}px`,
    '--widget-shadow-intensity': widgetSettings.shadowIntensity.toString(),
    '--widget-primary-color': 'var(--site-surface)',
    '--widget-secondary-color': 'var(--site-border)',
    '--widget-animation-duration': `${widgetSettings.animationDuration}ms`,
    '--widget-hover-opacity': hoverSettings.opacity.toString(),
    '--widget-hover-scale': hoverSettings.scale.toString()
  };
}

/**
 * Применяет настройки к элементу
 */
export function applyWidgetSettings(element: HTMLElement, settings: SiteSettings): void {
  const widgetSettings = getWidgetSettings(settings);
  const cssVars = generateWidgetCSSVariables(settings);

  // Применяем CSS переменные
  Object.entries(cssVars).forEach(([property, value]) => {
    element.style.setProperty(property, value);
  });

  // Применяем классы для эффектов
  if (widgetSettings.backgroundBlur) {
    element.classList.add('backdrop-blur-sm');
  } else {
    element.classList.remove('backdrop-blur-sm');
  }

  if (widgetSettings.hoverEffects) {
    element.classList.add('transition-all', 'duration-300');
  }
}

/**
 * Создает объект стилей для фона виджетов (без влияния на содержимое)
 */
export function createWidgetBackgroundStyles(settings: SiteSettings): React.CSSProperties {
  const widgetSettings = getWidgetSettings(settings);

  return {
    borderRadius: `${widgetSettings.borderRadius}px`,
    backgroundColor: `rgb(var(--site-surface-rgb) / ${widgetSettings.opacity})`,
    borderColor: 'var(--site-border)',
    transition: `all ${widgetSettings.animationDuration}ms ease`,
    boxShadow: widgetSettings.shadowIntensity > 0
      ? `0 10px 25px -5px rgba(0, 0, 0, ${widgetSettings.shadowIntensity})`
      : 'none'
  };
}

/**
 * Создает объект стилей для всего виджета (включая прозрачность содержимого)
 * @deprecated Использовать createWidgetBackgroundStyles для фона
 */
export function createWidgetStyles(settings: SiteSettings): React.CSSProperties {
  const widgetSettings = getWidgetSettings(settings);

  return {
    opacity: widgetSettings.opacity,
    borderRadius: `${widgetSettings.borderRadius}px`,
    backgroundColor: 'var(--site-surface)',
    borderColor: 'var(--site-border)',
    transition: `all ${widgetSettings.animationDuration}ms ease`,
    boxShadow: widgetSettings.shadowIntensity > 0
      ? `0 10px 25px -5px rgba(0, 0, 0, ${widgetSettings.shadowIntensity})`
      : 'none'
  };
}

/**
 * Хук для получения настроек виджетов
 */
export function useWidgetSettings(): SiteSettings | null {
  const [settings, setSettings] = useState<SiteSettings | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const siteSettings = await apiClient.getSiteSettings();
        setSettings(siteSettings);
      } catch (error) {
        console.warn('Не удалось загрузить настройки виджетов:', error);
      }
    };
    loadSettings();
  }, []);

  return settings;
}
